import { datadogRum } from '@datadog/browser-rum';
import {
  AccessToken,
  AuthApiError,
  AuthSdkError,
  IDToken,
  OAuthError,
  Token,
} from '@okta/okta-auth-js';
import oktaAuth, { clearAuthSubscribers } from 'okta/oktaConfig';
import z from 'zod';

import { handleApiError } from '@/utils/api-error-handler';
import {
  beginLogoutInProgress,
  clearLogoutInProgress,
  clearPendingLogoutStorageReset,
  consumePendingLogoutStorageReset as consumePendingLogoutStorageResetState,
  isLogoutInProgress,
  isSameOriginLogoutRedirect,
  scheduleLogoutInProgressClear,
  setPendingLogoutStorageReset,
  waitForLogoutInProgressToClear,
} from '@/utils/auth-session';
import { setWindowObjectToConnectAppToCypress } from '@/utils/environment';

import { queryClient } from '@/services/api';
import { NativeAndroidUser, OktaTokenStorage, OktaUserRaw } from '@/types';

import Constants from 'constants/index';

import { setImpersonatedToken } from './permissions-widget/token';

import { appInitializedAtom } from '@/app/state';
import { resetAllStorageAtoms } from '@/atoms/resetStorage';
import { store as jotaiStore } from '@/atoms/store';

export const setReturnTo = () => {
  if (!['/login', '/login/callback'].includes(window.location.pathname)) {
    const fullPath =
      window.location.pathname + window.location.search + window.location.hash;
    localStorage.setItem('returnTo', fullPath);
  }
};

const clearOktaLocalStorage = () => {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('okta')) localStorage.removeItem(k);
    }
  } catch (e) {
    if (e instanceof Error) {
      console.warn(`clearOktaLocalStorage failed ${e.message}`);
    } else {
      console.warn(`clearOktaLocalStorage failed ${e}`);
    }
  }
};

const getSignOutErrorMessage = (error: unknown): string =>
  error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : 'Unknown signOut error';

const isSignOutNavigationAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  const message = getSignOutErrorMessage(error).toLowerCase();
  return (
    message.includes('err_aborted') ||
    message.includes('failed to fetch') ||
    message.includes('network request failed') ||
    message.includes('aborterror') ||
    message.includes('load failed') ||
    message.includes('networkerror')
  );
};

export const logout = async () => {
  beginLogoutInProgress();
  resetAndroidBridgeCache();
  setImpersonatedToken(undefined);
  clearAuthSubscribers();

  try {
    await queryClient.cancelQueries();
  } catch (error) {
    console.warn('queryClient.cancelQueries failed during logout', error);
  }
  queryClient.removeQueries({ queryKey: ['auth'] });

  const logoutRedirectUri = new URL(
    Constants.PATHWAY_LOGOUT_REDIRECT ?? '/',
    window.location.origin,
  ).href;
  const shouldDeferStorageReset = isSameOriginLogoutRedirect(logoutRedirectUri);
  const signOutStartHref = window.location.href;
  let tokenManagerIdToken: IDToken | undefined;
  try {
    tokenManagerIdToken = (await oktaAuth.tokenManager.getTokens()).idToken;
  } catch {
    tokenManagerIdToken = undefined;
  }

  if (shouldDeferStorageReset) {
    // Same-origin redirects preserve stale Jotai storage for one boot cycle;
    // mark a reset for initializeApp to consume on the next load instead of
    // clearing atoms now (avoids a blank visual flash during the redirect).
    if (!setPendingLogoutStorageReset()) {
      // Marker write failed, so we cannot defer cleanup safely.
      // Fall back to immediate reset in this same execution path.
      resetAllStorageAtoms();
    }
  } else {
    resetAllStorageAtoms();
    clearPendingLogoutStorageReset();
  }

  try {
    // Preferred path: SDK-driven sign-out remains the primary integration.
    const signOutOptions = tokenManagerIdToken
      ? {
          postLogoutRedirectUri: logoutRedirectUri,
          idToken: tokenManagerIdToken,
          revokeAccessToken: false,
          revokeRefreshToken: false,
          clearTokensBeforeRedirect: true,
        }
      : {
          postLogoutRedirectUri: logoutRedirectUri,
          idToken: tokenManagerIdToken,
          clearTokensBeforeRedirect: true,
        };

    await oktaAuth.signOut(signOutOptions);
    // signOut may resolve before navigation fully commits; keep the guard
    // briefly to block background refresh/signInWithRedirect races.
    scheduleLogoutInProgressClear();
    return;
  } catch (e) {
    console.warn(
      'oktaAuth.signOut failed; falling back to redirect-based logout',
      e,
    );
    const isNavigationAbort = isSignOutNavigationAbortError(e);
    const hasNavigationStarted = window.location.href !== signOutStartHref;
    const signOutError =
      e instanceof Error ? e : new Error(getSignOutErrorMessage(e));
    datadogRum.addError(signOutError, {
      section: 'okta',
      originalErrorMessage: getSignOutErrorMessage(e),
      isSafariLoadFailed: /load failed/i.test(signOutError.message),
      isNavigationAbort,
    });

    // Browser navigation can abort the logout request while sign-out still
    // succeeds. In this case, avoid forcing re-login fallback.
    if (isNavigationAbort || hasNavigationStarted) {
      // Browser navigation can abort the logout request while sign-out still
      // succeeds. Even if the request was aborted, ensure local Okta tokens are
      // cleared so refresh/sign-in races can't resurrect the session.
      clearOktaLocalStorage();
      // Keep logout guard active briefly so in-flight token refresh requests
      // cannot trigger immediate signInWithRedirect loops.
      scheduleLogoutInProgressClear();
      return;
    }
  }

  queryClient.clear();
  clearOktaLocalStorage();
  resetAllStorageAtoms();
  clearPendingLogoutStorageReset();

  // Fallback path: no CORS dependency, forces fresh auth
  // Use a same-origin URI for originalUri: toRelativeUrl (used by restoreOriginalUri)
  // can throw or produce invalid navigation for cross-origin values.
  const safeOriginalUri = isSameOriginLogoutRedirect(logoutRedirectUri)
    ? logoutRedirectUri
    : '/';
  try {
    await oktaAuth.signInWithRedirect({
      originalUri: safeOriginalUri,
      prompt: 'login',
      maxAge: 0,
    });
    clearLogoutInProgress();
  } catch (redirectError) {
    clearLogoutInProgress();
    clearPendingLogoutStorageReset();
    resetAllStorageAtoms();
    throw redirectError;
  }
};

export const setReturnToAndLogout = async () => {
  setReturnTo();
  return logout();
};

/**
 * Clear Okta tokens from local storage before logout.
 * Use in error paths where stale tokens must be wiped before re-authentication
 * (e.g., OAuth callback parse failures).
 */
export const clearOktaAndLogout = async () => {
  clearOktaLocalStorage();
  return setReturnToAndLogout();
};

export const consumePendingLogoutStorageReset = (): boolean =>
  consumePendingLogoutStorageResetState({
    resetAllStorageAtoms,
    clearOktaLocalStorage,
  });

export const signInWithRedirect: typeof oktaAuth.signInWithRedirect =
  async opts => {
    if (isLogoutInProgress()) {
      return;
    }

    if (Constants.IS_IN_CYPRESS_TEST_LOCAL) {
      setWindowObjectToConnectAppToCypress('signInWithRedirect', 'true');
      return;
    }
    try {
      setReturnTo();
      await oktaAuth.signInWithRedirect(opts);
    } catch (err: unknown) {
      let status: number | undefined;
      let errorCode: string | undefined;
      let errorSummary: string | undefined;

      if (err instanceof AuthApiError || err instanceof AuthSdkError) {
        status = err.xhr?.status;
        errorCode = err.errorCode;
        errorSummary = err.errorSummary;
      } else if (err instanceof OAuthError) {
        errorCode = err.errorCode;
        errorSummary = err.errorSummary;
      }

      datadogRum.addError(err, {
        section: 'okta',
        status,
        errorCode,
        errorSummary,
      });
      await setReturnToAndLogout();
    }
  };

type TryRefreshResult =
  | { success: true; token: string }
  | { success: false; error: unknown };

/**
 * Attempt silent token refresh without auto-redirecting on failure.
 * Use this when you want to handle failure yourself (e.g., show a warning first).
 */
export const tryRefreshSilently = async (): Promise<TryRefreshResult> => {
  // Debug hook: allow forcing failure for testing (set via window.debugAuth.failNextRefresh())
  const w = window as { _debugFailNextRefresh?: boolean };
  if (w._debugFailNextRefresh) {
    w._debugFailNextRefresh = false;
    return { success: false, error: new Error('Forced failure for testing') };
  }

  try {
    // Uses the refresh token (via XHR POST /token) when present, which is
    // resilient to hard refreshes and not subject to Safari ITP / 3rd-party
    // cookie restrictions. Falls back to the iframe-based getWithoutPrompt
    // only if no refresh token exists (legacy sessions minted before the
    // offline_access scope was added).
    const renewed = (await oktaAuth.tokenManager.renew('accessToken')) as
      | AccessToken
      | undefined;

    const token = renewed?.accessToken;
    if (!token) {
      return {
        success: false,
        error: new Error('No access token after renew'),
      };
    }

    return { success: true, token };
  } catch (e) {
    return { success: false, error: e };
  }
};

/**
 * Silently refresh tokens using Okta's SSO session (iframe-based).
 * Falls back to full redirect login if SSO session has expired.
 *
 * Concurrent callers share one in-flight refresh/redirect so multiple API
 * failures do not start duplicate Okta authorize flows.
 *
 * @returns The new access token string, or undefined if redirecting
 */
let refreshInFlight: Promise<string | undefined> | undefined;

export const refreshTokensSilently = async (): Promise<string | undefined> => {
  refreshInFlight ??= refreshTokensSilentlyInternal().finally(() => {
    refreshInFlight = undefined;
  });

  return refreshInFlight;
};

const refreshTokensSilentlyInternal = async (): Promise<string | undefined> => {
  if (isLogoutInProgress()) {
    // logout() can keep this guard set briefly while browser navigation
    // settles. Wait for the guard window so callers that only trigger one
    // refresh attempt (e.g. SecureRoute) can recover without deadlocking.
    const hasClearedLogoutGuard = await waitForLogoutInProgressToClear();
    if (!hasClearedLogoutGuard) {
      return;
    }
  }

  if (Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST) {
    return;
  }

  const result = await tryRefreshSilently();
  if (result.success) {
    return result.token;
  }

  setReturnTo();
  await signInWithRedirect();
  return undefined;
};

export const isOktaTokenStorage = (
  value: unknown,
): value is OktaTokenStorage => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.accessToken && typeof v.accessToken === 'object') {
    const accessToken = v.accessToken as Record<string, unknown>;
    return typeof accessToken.accessToken === 'string';
  }
  return false;
};

/** Error has `status` `401`. */
export const isUnAuthorizedError = (error: unknown): boolean =>
  z.object({ status: z.number() }).safeParse(error).data?.status === 401;

interface NativeAuthNative {
  /** Whether or not the app is currently running in a native WebView. */
  isNative: true;
  /** The access token of the user from native. */
  accessToken: string;
  /** The auth user from native. */
  nativeUser: OktaUserRaw;
}

interface NativeAuthNonNative {
  isNative: false;
  accessToken: undefined;
  nativeUser: undefined;
}

export type NativeAuth = NativeAuthNative | NativeAuthNonNative;

interface AndroidBridgeCache {
  checked: boolean;
  user: NativeAndroidUser | undefined;
}

let androidBridgeCache: AndroidBridgeCache = {
  checked: false,
  user: undefined,
};

const resetAndroidBridgeCache = () => {
  androidBridgeCache = {
    checked: false,
    user: undefined,
  };
};

/** Get auth from the native app. */
export const getAuthFromNative = (): NativeAuth => {
  const androidUser = getAndroidUser();

  const user = window.userFromToken
    ? (JSON.parse(window.userFromToken) as OktaUserRaw)
    : androidUser?.userFromToken;

  const token =
    (window as { accessToken?: string }).accessToken ??
    androidUser?.accessToken;

  const isNative = user !== undefined && token !== undefined;

  if (isNative) {
    return {
      isNative: true,
      accessToken: token,
      nativeUser: user,
    };
  }

  return {
    isNative: false,
    accessToken: undefined,
    nativeUser: undefined,
  };
};

export const getAndroidUser = (): NativeAndroidUser | undefined => {
  if (androidBridgeCache.checked) {
    return androidBridgeCache.user;
  }

  androidBridgeCache.checked = true;
  const injectedObjectJson = window.ReactNativeWebView?.injectedObjectJson;
  if (!injectedObjectJson) {
    androidBridgeCache.user = undefined;
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(injectedObjectJson());
    const user =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as NativeAndroidUser)
        : undefined;

    androidBridgeCache.user = user;
    return user;
  } catch (error) {
    console.error('Native Error parsing Android user:', error);
    androidBridgeCache.user = undefined;
    return undefined;
  }
};

export const getApiToken = async (): Promise<string | undefined> => {
  try {
    if (Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST) {
      // parse stored okta token; use shared OktaTokenStorage type
      const rawStorage = JSON.parse(
        localStorage.getItem('okta-token-storage') ?? 'null',
      ) as unknown;
      const storage = isOktaTokenStorage(rawStorage) ? rawStorage : undefined;

      const token = storage?.accessToken?.accessToken;
      if (!token) {
        throw new Error('No access token found in storage');
      }
      return token;
    }

    const nativeAuth = getAuthFromNative();
    if (nativeAuth.isNative) {
      return nativeAuth.accessToken;
    }

    if (!jotaiStore.get(appInitializedAtom)) {
      throw new Error('Auth session not initialized');
    }

    const webToken = oktaAuth.getAccessToken();

    const tokens = oktaAuth.tokenManager.getTokensSync();
    if (
      (tokens.accessToken && getTokenExpiresInOneMinute(tokens.accessToken)) ||
      !webToken
    ) {
      const newToken = await refreshTokensSilently();
      if (newToken) {
        return newToken;
      }

      // signInWithRedirect already started in refreshTokensSilently — do not
      // throw (that surfaces as a Datadog error during in-flight heartbeats).
      console.debug(
        '[getApiToken] Session expired; re-auth redirect in progress',
      );
      return undefined;
    }

    return webToken;
  } catch (e: unknown) {
    console.log('[getApiToken] Error:', e);

    // Delegate to centralized error handler
    // Handles: skippable errors, Okta auth errors, 403 during impersonation,
    // 401 token errors (toast during impersonation, logout otherwise)
    await handleApiError(e, 'getApiToken');

    // If handleApiError didn't throw, the error wasn't auth-related
    // Just rethrow for the calling code to handle
    throw e;
  }
};

const getTokenExpiresInOneMinute = (token: Token): boolean => {
  const currentTime = Math.floor(Date.now() / 1000);
  const expiresAt = token.expiresAt;
  return expiresAt - currentTime <= 60;
};
